import { format, parseISO } from 'date-fns'
import { memo, useEffect, useMemo, useState } from 'react'
import sanitizeHtml from 'sanitize-html'
import { ansiParse } from '@/components/shared/logs-explorer/components/ansi-parse'
import { LogAttributes } from '@/components/shared/logs-explorer/components/log-attributes'
import { SeverityIndicator } from '@/components/shared/logs-explorer/components/severity'
import { StatusCodeIndicator } from '@/components/shared/logs-explorer/components/status-code-indicator'
import * as Styled from '@/components/shared/logs-explorer/components/styles'
import { useLogsExplorer } from '@/components/shared/logs-explorer/hooks/use-logs-explorer'
import { cleanMessage } from '@/components/shared/logs-explorer/utils/logs-to-text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Log } from '@/graphql.types'

type Props = {
  log: Log
  filterText?: string
  isHighlighted?: boolean
  isFirstLine?: boolean
  isLastLine?: boolean
  isExpanded: boolean
  onToggleExpand: () => void
}

const MAX_INLINE_MESSAGE_LENGTH = 2000

const escape = (value: string) => value.replace(/'/g, `\\'`)

const getTimestamp = (timestamp: string) => {
  const date = parseISO(timestamp.substring(0, 23) + 'Z')
  return format(date, 'MMM dd HH:mm:ss')
}

const formatMessageSize = (length: number) =>
  length >= 1024 ? `${Math.round(length / 1024)} KB` : `${length} B`

const LogLineComponent = ({
  log,
  filterText,
  isHighlighted = false,
  isFirstLine = false,
  isLastLine = false,
  isExpanded,
  onToggleExpand,
}: Props) => {
  const { config } = useLogsExplorer()
  const [highlightedHTML, setHighlightedHTML] = useState<string>('')

  const handleClick = () => {
    if (window.getSelection()?.toString()) {
      return
    }
    onToggleExpand()
  }

  const logMessage = log?.message ?? ''
  const isTruncated = logMessage.length > MAX_INLINE_MESSAGE_LENGTH
  const inlineMessage = isTruncated
    ? logMessage.slice(0, MAX_INLINE_MESSAGE_LENGTH)
    : logMessage

  useEffect(() => {
    let text = inlineMessage
    if (filterText) {
      const parts = escape(filterText ?? '').match(/"[^"]+"|[^ ]+/g) || []
      parts.forEach((part) => {
        const cleanedPart = part.replace(/(^"|"$)/g, '')
        const escapedPart = cleanedPart.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(escapedPart, 'gi')
        text = text.replace(
          regex,
          (match) =>
            `<span style="background-color: var(--destructive); color: white;">${match}</span>`
        )
      })
    }
    setHighlightedHTML(text)
  }, [inlineMessage, filterText])

  const renderedParts = useMemo(() => {
    const parts = ansiParse(highlightedHTML)
    return parts.map((part) => ({
      ...part,
      html: sanitizeHtml(part?.text ?? '', {
        allowedTags: ['span'],
        allowedAttributes: { span: ['style'] },
      }),
    }))
  }, [highlightedHTML])

  const isFullMessageVisible = isTruncated && isExpanded
  const fullMessage = useMemo(
    () => (isFullMessageVisible ? cleanMessage(logMessage) : ''),
    [isFullMessageVisible, logMessage]
  )
  const messageSize = formatMessageSize(logMessage.length)

  const indicator =
    config.indicatorMode === 'statusCode' ? (
      <StatusCodeIndicator
        statusCode={Number(
          (log?.attributes as Record<string, unknown>)?.edgeResponseStatus
        )}
      />
    ) : (
      <SeverityIndicator severity={log?.severity} />
    )

  return (
    <Styled.LogRowContent
      isHighlighted={isHighlighted}
      isFirstLine={isFirstLine}
      isLastLine={isLastLine}
      onClick={handleClick}
      className="cursor-pointer"
    >
      <Styled.ConsoleContent>
        <div className="flex gap-2 flex-nowrap items-start min-h-5">
          <div className="flex items-center gap-2 py-1">
            {indicator}
            <Styled.LinePart
              color="var(--muted-foreground)"
              className="shrink-0"
              style={{ userSelect: 'none' }}
            >
              {getTimestamp(log?.timestamp)}
            </Styled.LinePart>
          </div>
          <Styled.LogMessage
            data-log-message
            className={cn('min-h-7 py-1', isTruncated && 'pr-4')}
          >
            {renderedParts.map((part, index) => (
              <Styled.LinePart
                key={`line-${index}-part`}
                color={part.foreground}
                background={part.background}
                bold={part.bold}
                dangerouslySetInnerHTML={{ __html: part.html }}
              />
            ))}
            {isTruncated && (
              <Styled.LinePart color="var(--muted-foreground)">
                …
              </Styled.LinePart>
            )}
          </Styled.LogMessage>
        </div>
      </Styled.ConsoleContent>
      {isTruncated && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="img"
                tabIndex={0}
                aria-label={`Preview only, the full message is ${messageSize}`}
                className="absolute top-3 right-2.5 size-2 rounded-full bg-amber-500 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
            </TooltipTrigger>
            <TooltipContent side="left" className="font-sans">
              Preview only. Open the line to see the full message ({messageSize}
              ).
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {isExpanded && (
        <div
          className="col-span-full px-2"
          onClick={(e) => e.stopPropagation()}
        >
          {isFullMessageVisible && (
            <div className="mt-1.5 mb-2" data-log-full-message>
              <div className="mb-1.5 font-sans text-xs text-muted-foreground">
                Full message · {messageSize} · colors removed
              </div>
              <pre className="max-h-96 overflow-y-auto rounded-md border bg-muted/50 p-2 text-xs whitespace-pre-wrap [overflow-wrap:anywhere] cursor-text">
                {fullMessage}
              </pre>
            </div>
          )}
          <LogAttributes
            idLog={log?.id}
            severity={log?.severity}
            timestamp={log?.timestamp}
            attributes={log?.attributes as Record<string, unknown>}
            tags={log?.tags as Record<string, unknown>}
          />
        </div>
      )}
    </Styled.LogRowContent>
  )
}

LogLineComponent.displayName = 'LogLine'

export const LogLine = memo(LogLineComponent)
